import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    fetchTasks, createTask, deleteTask,
    type TaskApiRecord, type TaskStatus, type TaskPriority,
    type CreateTaskPayload,
} from '../../services/taskApi';
import { fetchStaffList, type StaffApiRecord } from '../../services/staffApi';
import './TaskManagement.css';

/* ═══════════════════════════════════════
   Label maps
   ═══════════════════════════════════════ */
const statusLabels: Record<TaskStatus, string> = {
    pending: 'Pending',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
};

const priorityLabels: Record<TaskPriority, string> = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
};

/* ═══════════════════════════════════════
   Searchable dropdown (filter by user)
   ═══════════════════════════════════════ */
type SelectOption = { value: string; label: string };

function SearchableSelect({
    options,
    value,
    onChange,
    placeholder,
    searchPlaceholder,
    searchable = true,
    block = false,
    id,
}: {
    options: SelectOption[];
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
    searchPlaceholder?: string;
    searchable?: boolean;
    block?: boolean;
    id?: string;
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
                setOpen(false);
                setQuery('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const selected = options.find((o) => o.value === value);
    const filtered = searchable && query.trim()
        ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
        : options;

    return (
        <div className={`task-mgmt__ss${block ? ' task-mgmt__ss--block' : ''}`} ref={wrapRef}>
            <button
                type="button"
                id={id}
                className="task-mgmt__ss-trigger"
                onClick={() => setOpen((o) => !o)}
            >
                <span className={selected ? '' : 'task-mgmt__ss-placeholder'}>
                    {selected ? selected.label : placeholder}
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
            {open && (
                <div className="task-mgmt__ss-menu">
                    {searchable && (
                        <input
                            className="task-mgmt__ss-search"
                            type="text"
                            autoFocus
                            placeholder={searchPlaceholder}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    )}
                    <div className="task-mgmt__ss-list">
                        {filtered.length === 0 ? (
                            <div className="task-mgmt__ss-empty">No matches</div>
                        ) : (
                            filtered.map((o) => (
                                <button
                                    type="button"
                                    key={o.value}
                                    className={`task-mgmt__ss-option${o.value === value ? ' task-mgmt__ss-option--active' : ''}`}
                                    onClick={() => {
                                        onChange(o.value);
                                        setOpen(false);
                                        setQuery('');
                                    }}
                                >
                                    {o.label}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ═══════════════════════════════════════
   Component
   ═══════════════════════════════════════ */
export default function TaskManagement() {
    const { t } = useTranslation();

    /* ── Data state ── */
    const [tasks, setTasks] = useState<TaskApiRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    /* ── Pagination ── */
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);

    /* ── Filters ── */
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [filterPriority, setFilterPriority] = useState<string>('all');
    const [filterAssignee, setFilterAssignee] = useState<string>('');

    /* ── Drawer state ── */
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    /* ── Staff list for assignee dropdown ── */
    const [staffList, setStaffList] = useState<StaffApiRecord[]>([]);
    const [loadingStaff, setLoadingStaff] = useState(false);

    /* ── Form state ── */
    const [formTitle, setFormTitle] = useState('');
    const [formDesc, setFormDesc] = useState('');
    const [formAssignee, setFormAssignee] = useState('');
    const [formStatus, setFormStatus] = useState<TaskStatus>('pending');
    const [formPriority, setFormPriority] = useState<TaskPriority>('medium');
    const [formDueDate, setFormDueDate] = useState('');

    /* ── Load tasks ── */
    const loadTasks = (p: number = page) => {
        setLoading(true);
        fetchTasks({
            page: p,
            perPage: 20,
            assigned_to: filterAssignee || undefined,
            status: filterStatus === 'all' ? undefined : (filterStatus as TaskStatus),
            priority: filterPriority === 'all' ? undefined : (filterPriority as TaskPriority),
            title: searchQuery.trim() || undefined,
        })
            .then((res) => {
                setTasks(res.data);
                if (res.meta) {
                    setLastPage(res.meta.last_page);
                    setPage(res.meta.current_page);
                }
            })
            .catch(() => {
                showToast(t('tasks.failedToLoad'), 'error');
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadStaff();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Reload when filters change (debounce search) ── */
    useEffect(() => {
        const handle = setTimeout(() => loadTasks(1), searchQuery ? 350 : 0);
        return () => clearTimeout(handle);
    }, [filterStatus, filterPriority, filterAssignee, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Load staff when drawer opens ── */
    const loadStaff = () => {
        if (staffList.length > 0) return;
        setLoadingStaff(true);
        fetchStaffList({ perPage: 200 })
            .then((res) => setStaffList(res.data))
            .catch(() => { /* silently ignore */ })
            .finally(() => setLoadingStaff(false));
    };

    /* ── Toast helper ── */
    const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
        if (type === 'error') setErrorMsg(msg); else setSuccessMsg(msg);
        setTimeout(() => { setSuccessMsg(''); setErrorMsg(''); }, 5000);
    };

    /* ── Open drawer ── */
    const openDrawer = () => {
        setFormTitle('');
        setFormDesc('');
        setFormAssignee('');
        setFormStatus('pending');
        setFormPriority('medium');
        setFormDueDate('');
        setErrorMsg('');
        setDrawerOpen(true);
        loadStaff();
    };

    /* ── Submit task ── */
    const handleSubmit = async () => {
        if (!formTitle.trim() || !formAssignee || !formDueDate) return;
        setSaving(true);
        setErrorMsg('');

        try {
            const payload: CreateTaskPayload = {
                title: formTitle,
                description: formDesc,
                assigned_to: formAssignee,
                status: formStatus,
                priority: formPriority,
                due_date: formDueDate.includes('T') ? formDueDate : `${formDueDate}T00:00:00`,
            };

            await createTask(payload);
            setDrawerOpen(false);
            showToast(t('tasks.createdSuccess'));
            loadTasks(1);
        } catch (err: unknown) {
            const apiErr = err as { errors?: Record<string, string[]>; message?: string };
            if (apiErr.errors) {
                setErrorMsg(Object.values(apiErr.errors).flat().join(' '));
            } else {
                setErrorMsg(apiErr.message || t('tasks.failedToCreate'));
            }
        } finally {
            setSaving(false);
        }
    };

    /* ── Delete task ── */
    const handleDelete = async (id: string) => {
        try {
            await deleteTask(id);
            setTasks((prev) => prev.filter((t) => t.id !== id));
            showToast(t('tasks.removedSuccess'));
        } catch {
            showToast(t('tasks.failedToDelete'), 'error');
        }
    };

    const formValid = formTitle.trim().length > 0 && formAssignee && formDueDate;

    /* ── Filtering happens server-side via fetchTasks params ── */
    const filteredTasks = tasks;

    /* ── Filter dropdown options ── */
    const statusOptions = [
        { value: 'all', label: t('tasks.allStatuses') },
        { value: 'pending', label: t('tasks.statusPending') },
        { value: 'in_progress', label: t('tasks.statusInProgress') },
        { value: 'completed', label: t('tasks.statusCompleted') },
        { value: 'cancelled', label: t('tasks.statusCancelled') },
    ];

    const priorityOptions = [
        { value: 'all', label: t('tasks.allPriorities') },
        { value: 'low', label: t('tasks.priorityLow') },
        { value: 'medium', label: t('tasks.priorityMedium') },
        { value: 'high', label: t('tasks.priorityHigh') },
    ];

    const assigneeOptions = [
        { value: '', label: t('tasks.allAssignees', 'All assignees') },
        ...staffList.map((s) => ({
            value: String(s.user.id),
            label: `${s.user.first_name} ${s.user.last_name || ''}`.trim(),
        })),
    ];

    /* ── Drawer form dropdown options ── */
    const formAssigneeOptions = staffList.map((s) => ({
        value: String(s.user.id),
        label: `${s.user.first_name} ${s.user.last_name || ''} ${s.employee_id ? `(${s.employee_id})` : ''}`.replace(/\s+/g, ' ').trim(),
    }));

    const formStatusOptions = [
        { value: 'pending', label: t('tasks.statusPending') },
        { value: 'in_progress', label: t('tasks.statusInProgress') },
        { value: 'completed', label: t('tasks.statusCompleted') },
        { value: 'cancelled', label: t('tasks.statusCancelled') },
    ];

    const formPriorityOptions = [
        { value: 'low', label: t('tasks.priorityLow') },
        { value: 'medium', label: t('tasks.priorityMedium') },
        { value: 'high', label: t('tasks.priorityHigh') },
    ];

    /* ── Format date display ── */
    const formatDate = (dateStr: string) => {
        try {
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch {
            return dateStr;
        }
    };

    /* ── Get assignee name ── */
    const getAssigneeName = (task: TaskApiRecord) => {
        const a = task.assigned_to ?? task.assignee ?? null;
        if (!a) return '—';
        return `${a.first_name} ${a.last_name || ''}`.trim() || '—';
    };

    return (
        <div className="task-mgmt">
            {/* Toasts */}
            {successMsg && (
                <div className="task-mgmt__toast">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    {successMsg}
                    <button className="task-mgmt__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}
            {errorMsg && !drawerOpen && (
                <div className="task-mgmt__toast task-mgmt__toast--error">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                    {errorMsg}
                    <button className="task-mgmt__toast-dismiss" onClick={() => setErrorMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {/* Header */}
            <div className="task-mgmt__header">
                <div className="task-mgmt__header-text">
                    <h1 className="task-mgmt__title">{t('tasks.title')}</h1>
                    <p className="task-mgmt__subtitle">{t('tasks.subtitle')}</p>
                </div>
                <div className="task-mgmt__header-actions">
                    <button className="task-mgmt__add-btn" onClick={openDrawer}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        {t('tasks.addTask')}
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="task-mgmt__filters">
                <div className="task-mgmt__search">
                    <svg className="task-mgmt__search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                    <input
                        className="task-mgmt__search-input"
                        type="text"
                        placeholder={t('tasks.searchPlaceholder', 'Search by task name')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button
                            className="task-mgmt__search-clear"
                            onClick={() => setSearchQuery('')}
                            aria-label="Clear search"
                        >✕</button>
                    )}
                </div>
                <SearchableSelect
                    options={statusOptions}
                    value={filterStatus}
                    onChange={setFilterStatus}
                    placeholder={t('tasks.allStatuses')}
                    searchable={false}
                />
                <SearchableSelect
                    options={priorityOptions}
                    value={filterPriority}
                    onChange={setFilterPriority}
                    placeholder={t('tasks.allPriorities')}
                    searchable={false}
                />
                <SearchableSelect
                    options={assigneeOptions}
                    value={filterAssignee}
                    onChange={setFilterAssignee}
                    placeholder={t('tasks.allAssignees', 'All assignees')}
                    searchPlaceholder={t('tasks.searchAssignee', 'Search user...')}
                />
            </div>

            {/* ═══ Content Area ═══ */}
            {loading ? (
                <div className="task-mgmt__loading">
                    <div className="task-mgmt__loader">
                        <span className="task-mgmt__loader-dot" />
                        <span className="task-mgmt__loader-dot" />
                        <span className="task-mgmt__loader-dot" />
                    </div>
                </div>
            ) : filteredTasks.length === 0 ? (
                <div className="task-mgmt__empty-state">
                    <div className="task-mgmt__empty-icon">
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 11l3 3L22 4" />
                            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                        </svg>
                    </div>
                    <h3 className="task-mgmt__empty-title">{t('tasks.noTasks')}</h3>
                    <p className="task-mgmt__empty-text">{t('tasks.createFirst')}</p>
                </div>
            ) : (
                <>
                    <div className="task-mgmt__grid-wrap">
                        <table className="task-mgmt__table">
                            <thead>
                                <tr>
                                    <th className="task-mgmt__th">{t('tasks.taskTitle')}</th>
                                    <th className="task-mgmt__th">{t('tasks.description')}</th>
                                    <th className="task-mgmt__th">{t('tasks.assignee')}</th>
                                    <th className="task-mgmt__th">{t('tasks.status')}</th>
                                    <th className="task-mgmt__th">{t('tasks.priority')}</th>
                                    <th className="task-mgmt__th">{t('tasks.dueDate')}</th>
                                    <th className="task-mgmt__th task-mgmt__th--actions">{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTasks.map((task) => (
                                    <tr className="task-mgmt__row" key={task.id}>
                                        <td className="task-mgmt__td task-mgmt__td--title">{task.title}</td>
                                        <td className="task-mgmt__td task-mgmt__td--desc" title={task.description || ''}>
                                            {task.description || '—'}
                                        </td>
                                        <td className="task-mgmt__td">{getAssigneeName(task)}</td>
                                        <td className="task-mgmt__td">
                                            <span className={`task-mgmt__badge task-mgmt__badge--${task.status}`}>
                                                {statusLabels[task.status] || task.status}
                                            </span>
                                        </td>
                                        <td className="task-mgmt__td">
                                            <span className={`task-mgmt__badge task-mgmt__badge--${task.priority}`}>
                                                {priorityLabels[task.priority] || task.priority}
                                            </span>
                                        </td>
                                        <td className="task-mgmt__td task-mgmt__td--date">
                                            {formatDate(task.due_date)}
                                        </td>
                                        <td className="task-mgmt__td" style={{ textAlign: 'center' }}>
                                            <button
                                                className="task-mgmt__action-btn"
                                                onClick={() => handleDelete(task.id)}
                                                title={t('common.delete')}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {lastPage > 1 && (
                        <div className="task-mgmt__pagination">
                            <button
                                className="task-mgmt__page-btn"
                                disabled={page <= 1}
                                onClick={() => loadTasks(page - 1)}
                            >
                                {t('staffMgmt.prev', 'Prev')}
                            </button>
                            <span className="task-mgmt__page-info">
                                {t('staffMgmt.pageOf', { page, total: lastPage })}
                            </span>
                            <button
                                className="task-mgmt__page-btn"
                                disabled={page >= lastPage}
                                onClick={() => loadTasks(page + 1)}
                            >
                                {t('staffMgmt.next', 'Next')}
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* ═══ Creation Drawer ═══ */}
            {drawerOpen && (
                <>
                    <div className="task-mgmt__drawer-overlay" onClick={() => setDrawerOpen(false)} />
                    <div className="task-mgmt__drawer">
                        <div className="task-mgmt__drawer-header">
                            <h2 className="task-mgmt__drawer-title">{t('tasks.addTask')}</h2>
                            <button className="task-mgmt__drawer-close" onClick={() => setDrawerOpen(false)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>

                        <div className="task-mgmt__drawer-body">
                            {errorMsg && (
                                <div className="task-mgmt__toast task-mgmt__toast--error" style={{ position: 'static', animation: 'none' }}>
                                    {errorMsg}
                                </div>
                            )}

                            <div className="task-mgmt__drawer-card">
                                <span className="task-mgmt__drawer-card-title">{t('tasks.taskDetails')}</span>

                                <div className="task-mgmt__field">
                                    <label className="task-mgmt__label" htmlFor="taskTitle">{t('tasks.taskTitle')} *</label>
                                    <input
                                        className="task-mgmt__input"
                                        id="taskTitle"
                                        type="text"
                                        placeholder={t('tasks.titlePlaceholder')}
                                        maxLength={255}
                                        value={formTitle}
                                        onChange={(e) => setFormTitle(e.target.value)}
                                    />
                                </div>

                                <div className="task-mgmt__field">
                                    <label className="task-mgmt__label" htmlFor="taskDesc">{t('tasks.description')}</label>
                                    <textarea
                                        className="task-mgmt__textarea"
                                        id="taskDesc"
                                        placeholder={t('tasks.descPlaceholder')}
                                        value={formDesc}
                                        onChange={(e) => setFormDesc(e.target.value)}
                                    />
                                </div>

                                <div className="task-mgmt__field">
                                    <label className="task-mgmt__label" htmlFor="taskAssignee">{t('tasks.assignee')} *</label>
                                    <SearchableSelect
                                        id="taskAssignee"
                                        block
                                        options={formAssigneeOptions}
                                        value={formAssignee}
                                        onChange={setFormAssignee}
                                        placeholder={loadingStaff ? t('common.loading') : t('tasks.selectAssignee')}
                                        searchPlaceholder={t('tasks.searchAssignee', 'Search user...')}
                                    />
                                </div>

                                <div className="task-mgmt__field-row">
                                    <div className="task-mgmt__field">
                                        <label className="task-mgmt__label" htmlFor="taskStatus">{t('tasks.status')}</label>
                                        <SearchableSelect
                                            id="taskStatus"
                                            block
                                            searchable={false}
                                            options={formStatusOptions}
                                            value={formStatus}
                                            onChange={(v) => setFormStatus(v as TaskStatus)}
                                            placeholder={t('tasks.status')}
                                        />
                                    </div>
                                    <div className="task-mgmt__field">
                                        <label className="task-mgmt__label" htmlFor="taskPriority">{t('tasks.priority')}</label>
                                        <SearchableSelect
                                            id="taskPriority"
                                            block
                                            searchable={false}
                                            options={formPriorityOptions}
                                            value={formPriority}
                                            onChange={(v) => setFormPriority(v as TaskPriority)}
                                            placeholder={t('tasks.priority')}
                                        />
                                    </div>
                                </div>

                                <div className="task-mgmt__field">
                                    <label className="task-mgmt__label" htmlFor="taskDueDate">{t('tasks.dueDate')} *</label>
                                    <input
                                        className="task-mgmt__input"
                                        id="taskDueDate"
                                        type="datetime-local"
                                        value={formDueDate}
                                        onChange={(e) => setFormDueDate(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="task-mgmt__drawer-footer">
                            <button className="task-mgmt__drawer-cancel" onClick={() => setDrawerOpen(false)}>
                                {t('common.cancel')}
                            </button>
                            <button
                                className="task-mgmt__drawer-save"
                                onClick={handleSubmit}
                                disabled={!formValid || saving}
                            >
                                {saving ? t('common.saving') : t('tasks.addTask')}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
